// src/pages/CartoonPage.jsx
//
// Comic reader: character introduction, then an episode reader with
// swipe-through panels, explicit previous/next controls, lazy loading and a
// separate enlarge control.

import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Keyboard, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import LoadingSpinner from "../components/LoadingSpinner";
import Lightbox from "../components/Lightbox";
import AomiIntro from "../components/AomiIntro";
import { fetchCartoonEpisodes } from "../api";
import { imgurSize } from "../utils/imgur";
import "../App.css";

// An episode counts as "real" if it has at least one non-empty image_url.
function isReady(episode) {
  return episode.panels.some((p) => p && p.image_url);
}

const PANEL_FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 800"><rect width="600" height="800" fill="#1a1a40"/><text x="300" y="400" fill="#fff" font-family="sans-serif" font-size="20" text-anchor="middle">Panel unavailable</text></svg>`
  );

export default function CartoonPage() {
  const [episodes, setEpisodes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [panelIndex, setPanelIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const swiperRef = useRef(null);
  const readerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCartoonEpisodes();
        if (!cancelled) {
          setEpisodes(data || []);
          setCurrentIndex(0);
        }
      } catch (err) {
        console.error("Error fetching episodes:", err);
        if (!cancelled) {
          setError(
            err.status === 0
              ? "Could not reach the story. Check your connection and try again."
              : "The episodes couldn't be loaded just now. Please try again shortly."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const readyIndices = useMemo(
    () => episodes.map((e, i) => (isReady(e) ? i : -1)).filter((i) => i !== -1),
    [episodes]
  );

  const nextReadyIndex = useCallback(
    (from) => {
      const next = readyIndices.find((i) => i > from);
      return next === undefined ? null : next;
    },
    [readyIndices]
  );

  const goToEpisode = useCallback((idx) => {
    setCurrentIndex((prev) => (prev === idx ? prev : idx));
    setPanelIndex(0);
    requestAnimationFrame(() => {
      swiperRef.current?.slideTo(0, 0);
    });
  }, []);

  /* AUTO-ADVANCE REMOVED.
     onReachEnd fires the moment you ARRIVE at the last panel, not when you
     swipe past it — so the reader got roughly 400ms with the final panel
     before being thrown into the next episode. The last panel of a comic is
     usually the one that lands; it should wait.

     The "Continue to …" button below does the same job explicitly, says where
     it goes, and lets the reader decide when they're finished. */

  const scrollToReader = useCallback(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    readerRef.current?.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }, []);

  const goPrev = useCallback(() => swiperRef.current?.slidePrev(), []);
  const goNext = useCallback(() => swiperRef.current?.slideNext(), []);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="cartoon-reader cartoon-message">
        <p role="alert">{error}</p>
        <button
          type="button"
          className="ep-continue"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    );
  }

  const currentEpisode = episodes[currentIndex];
  const isCurrentReady = currentEpisode ? isReady(currentEpisode) : false;
  const nextIdx = nextReadyIndex(currentIndex);
  const panelCount = currentEpisode ? currentEpisode.panels.length : 0;
  const currentPanel = currentEpisode?.panels?.[panelIndex];

  return (
    <div className="cartoon-reader">
      <div className="cartoon-header">
        <h1 className="cartoon-title">Echoes of the Threads</h1>
      </div>

      <AomiIntro onBeginReading={scrollToReader} />

      <div className="cartoon-reader-body" ref={readerRef} tabIndex={-1}>
        {episodes.length === 0 ? (
          <div className="cartoon-coming-soon">
            <p>No episodes have been published yet.</p>
            <p className="cartoon-coming-soon-sub">
              The first thread is still being woven — check back soon.
            </p>
          </div>
        ) : (
          <>
            {/* The pills WRAP rather than scroll horizontally. The scrolling
                strip saved vertical space, but a horizontal scroller inside a
                vertically scrolling page is fragile on touch — the browser has
                to guess which direction you meant, and it frequently guesses
                wrong. Two rows of pills that always work beats one row that
                sometimes doesn't. */}
            <nav className="cartoon-episode-menu" aria-label="Episode selection">
              {episodes.map((ep, idx) => {
                const ready = isReady(ep);
                const active = idx === currentIndex;
                return (
                  <button
                    key={ep.id}
                    type="button"
                    className={`ep-pill ${active ? "active" : ""} ${ready ? "" : "disabled"}`}
                    onClick={() => ready && goToEpisode(idx)}
                    disabled={!ready}
                    aria-current={active ? "true" : undefined}
                    aria-label={
                      ready
                        ? `Episode ${idx + 1}: ${ep.title}`
                        : `Episode ${idx + 1}: ${ep.title} — coming soon`
                    }
                  >
                    <span className="ep-num">Ep {idx + 1}</span>
                    <span className="ep-name">{ep.title.split(":").pop().trim()}</span>
                    {!ready && <span className="ep-soon">soon</span>}
                  </button>
                );
              })}
            </nav>

            <div className="cartoon-meta">
              <h2 className="cartoon-subtitle">{currentEpisode.title}</h2>
              <details className="cartoon-details">
                <summary>About this episode</summary>
                <p className="cartoon-description">{currentEpisode.description}</p>
              </details>
            </div>

            <div className="cartoon-stage">
              {!isCurrentReady ? (
                <div className="cartoon-coming-soon">
                  <p>This episode is still being woven.</p>
                  <p className="cartoon-coming-soon-sub">
                    Check back soon, or read another episode above.
                  </p>
                </div>
              ) : (
                <Swiper
                  key={currentEpisode.id}
                  modules={[Pagination, Keyboard, A11y]}
                  pagination={{ clickable: true, dynamicBullets: panelCount > 8 }}
                  keyboard={{ enabled: true }}
                  a11y={{
                    prevSlideMessage: "Previous panel",
                    nextSlideMessage: "Next panel",
                  }}
                  spaceBetween={0}
                  slidesPerView={1}
                  onSwiper={(s) => {
                    swiperRef.current = s;
                  }}
                  onSlideChange={(s) => setPanelIndex(s.activeIndex)}
                >
                  {currentEpisode.panels.map((panel, idx) => (
                    <SwiperSlide key={idx}>
                      <div className="panel-wrapper">
                        {panel.image_url ? (
                          /* A PLAIN <img>, not wrapped in a <button>.
                             The button was there so the zoom view was
                             keyboard-reachable, but a button treats a drag as
                             a press and swallowed it — which is why swiping
                             stopped working. Swiper needs the touch to reach
                             the slide.

                             Keyboard access now comes from the separate
                             "Enlarge" control below the panel, which is a
                             better affordance anyway: nothing previously told
                             anyone the image was tappable. */
                          <img
                            src={imgurSize(panel.image_url, "h")}
                            alt={panel.caption || `Panel ${idx + 1}`}
                            className="panel-image"
                            loading={idx === 0 ? "eager" : "lazy"}
                            decoding="async"
                            draggable={false}
                            onError={(e) => {
                              console.error("Image failed:", panel.image_url);
                              e.target.src = PANEL_FALLBACK;
                            }}
                          />
                        ) : (
                          <div className="panel-placeholder">Panel coming soon</div>
                        )}
                        {panel.caption && (
                          <p className="panel-caption">{panel.caption}</p>
                        )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>

            {isCurrentReady && (
              <div className="cartoon-footer">
                {/* Explicit controls. Swiping still works, but it is no longer
                    the only way through — and these give keyboard and
                    screen-reader users a real control rather than a gesture
                    they can't perform. */}
                <div className="panel-nav">
                  <button
                    type="button"
                    className="panel-nav-button"
                    onClick={goPrev}
                    disabled={panelIndex === 0}
                    aria-label="Previous panel"
                  >
                    ←
                  </button>

                  <p className="panel-counter" aria-live="polite">
                    Panel {panelIndex + 1} of {panelCount}
                  </p>

                  <button
                    type="button"
                    className="panel-nav-button"
                    onClick={goNext}
                    disabled={panelIndex === panelCount - 1}
                    aria-label="Next panel"
                  >
                    →
                  </button>
                </div>

                {/* Zoom, as its own labelled control rather than a hidden tap
                    target on the image. */}
                {currentPanel?.image_url && (
                  <button
                    type="button"
                    className="panel-enlarge"
                    onClick={() => setLightboxSrc(currentPanel.image_url)}
                  >
                    ⛶ Enlarge panel
                  </button>
                )}

                {panelIndex === 0 && panelCount > 1 && (
                  <p className="swipe-hint" aria-hidden="true">
                    Swipe, tap the arrows, or use ← →
                  </p>
                )}

                {panelIndex === panelCount - 1 &&
                  (nextIdx !== null ? (
                    <button
                      type="button"
                      className="ep-continue"
                      onClick={() => goToEpisode(nextIdx)}
                    >
                      Continue to {episodes[nextIdx].title.split(":")[0] || "next episode"} →
                    </button>
                  ) : (
                    <p className="ep-end">
                      You've reached the end of what's been woven so far.
                    </p>
                  ))}
              </div>
            )}
          </>
        )}
      </div>

      {lightboxSrc && (
        <Lightbox
          src={lightboxSrc}
          alt="Cartoon panel"
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </div>
  );
}